let toastEl = null;
let toastTimeout = null;

// Messages that describe an operation still IN PROGRESS. These stay on screen
// (no auto-dismiss) until the next toast — the completion/error message —
// replaces them, so "Uploading…" / "Saving…" / "Generating…" never vanishes
// mid-operation leaving the user unsure whether it stalled. A generous fallback
// still clears them if a caller forgets to post a completion toast, so a stuck
// job can't pin the toast open.
//
// Heuristic: any message whose first word is a gerund (ends in "-ing") is
// treated as in-progress — this covers ANY "…ing" action toast generically
// (Uploading, Deleting, Archiving, Rewriting, Publishing…) rather than a fixed
// list. A short stoplist excludes "-ing" words that aren't progress verbs.
const GERUND_STOPLIST = new Set([
  'nothing', 'something', 'anything', 'everything',
  'warning', 'missing', 'morning', 'evening',
]);
const ELLIPSIS_RE = /(…|\.\.\.)\s*$/;
const AUTO_DISMISS_MS = 3200;
const PROGRESS_FALLBACK_MS = 60000; // safety net so an errored job can't pin it

function isProgressMessage(text) {
  if (ELLIPSIS_RE.test(text)) return true;
  const first = (text.trim().match(/^[A-Za-z]+/) || [''])[0].toLowerCase();
  return first.length > 4 && first.endsWith('ing') && !GERUND_STOPLIST.has(first);
}

/**
 * Show a transient toast.
 * @param {string} msg
 * @param {object|boolean|number} [opts] - true/{sticky:true} to force-persist,
 *   a number to override the auto-dismiss ms, or omit for the default heuristic
 *   (progress-style messages persist; everything else auto-dismisses).
 */
export function toast(msg, opts) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'toast';
    // Announce save/notify confirmations to screen readers (UI audit).
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.setAttribute('aria-atomic', 'true');
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = msg;
  toastEl.classList.add('show');
  clearTimeout(toastTimeout);

  const text = String(msg || '');
  const forcedSticky = opts === true || (opts && typeof opts === 'object' && opts.sticky);
  const explicitMs = typeof opts === 'number' ? opts : (opts && typeof opts === 'object' ? opts.duration : undefined);
  const isProgress = forcedSticky || isProgressMessage(text);

  const ms = explicitMs != null ? explicitMs : (isProgress ? PROGRESS_FALLBACK_MS : AUTO_DISMISS_MS);
  if (ms === Infinity) return; // truly sticky - caller will dismiss/replace
  toastTimeout = setTimeout(() => toastEl.classList.remove('show'), ms);
}

/** Explicitly hide the current toast (e.g. cancel an in-progress message). */
export function hideToast() {
  if (toastEl) toastEl.classList.remove('show');
  clearTimeout(toastTimeout);
}
